import clsx from 'clsx';
import type React from 'react';
import { Badge } from '../Badge';
import styles from './Gallery.module.scss';

type GalleryImageType = 'image' | 'video' | '360' | 'sphere';

interface ResponsiveSource {
  src: string;
  srcSet?: string;
  media?: string;
  type?: string;
}

interface GalleryImageProps {
  src: string;
  srcSet?: string;
  sources?: ResponsiveSource[];
  alt: string;
  type?: GalleryImageType;
  sizes?: string;
  width?: number;
  height?: number;
}

interface GalleryProps {
  images: GalleryImageProps[];
  action?: React.ReactNode;
  ariaLabel: string;
  testId: string;
  className?: string;
  dominantColor?: string;
  onClick?: () => void;
}

const getMediaBadge = (type: GalleryImageType) => {
  switch (type) {
    case 'video':
      return (
        <Badge tone="brand" size="sm" className={styles.badge}>
          Video
        </Badge>
      );
    case '360':
    case 'sphere':
      return (
        <Badge tone="brand" size="sm" className={styles.badge}>
          360°
        </Badge>
      );
    default:
      return null;
  }
};

export const Gallery: React.FC<GalleryProps> = ({
  images,
  action,
  ariaLabel,
  testId,
  className,
  dominantColor,
  onClick,
}) => {
  const imagesToDisplay = images.slice(0, 5);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (onClick && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onClick();
    }
  };

  const galleryProps = onClick
    ? {
        onClick,
        onKeyDown: handleKeyDown,
      }
    : {};

  return (
    <section
      className={clsx(styles.gallery, { [styles.galleryClickable]: !!onClick }, className)}
      aria-label={ariaLabel}
      data-testid={testId}
      {...galleryProps}
    >
      <ul
        className={clsx(
          styles.list,
          styles[`list-${imagesToDisplay.length > 5 ? 5 : imagesToDisplay.length}`],
        )}
      >
        {imagesToDisplay.map((image, index) => (
          <li className={styles.item} key={image.src}>
            <picture>
              {image.sources?.map((source, sourceIndex) => (
                <source
                  key={sourceIndex}
                  srcSet={source.srcSet || source.src}
                  media={source.media}
                  type={source.type}
                />
              ))}
              <img
                src={image.src}
                srcSet={image.srcSet}
                alt={image.alt}
                sizes={image.sizes}
                className={styles.image}
                data-testid={`${testId}-image-${index}`}
                fetchPriority={index === 0 ? 'high' : 'auto'}
                style={{ '--_dominant-color': dominantColor } as React.CSSProperties}
                width="840"
                height="630"
              />
            </picture>
            {getMediaBadge(image.type || 'image')}
          </li>
        ))}
      </ul>
      {action && <div className={styles.action}>{action}</div>}
    </section>
  );
};
